/**
 * Telemetry Trimming Stage
 *
 * Trims raw telemetry to the racing range by detecting finish line crossings
 * in GPS data before expensive sensor fusion processing. This avoids processing
 * warm-up/cool-down data that will be discarded during lap detection.
 *
 * Cost: ~5ms for a 46K-point session (just GPS coordinate scanning).
 * Savings: Proportional to the fraction of non-racing data removed — typically
 * 50-65% fewer points through sensor fusion.
 */

import {
  PipelineStage,
  PipelineContext,
  StageResult,
} from '../ProcessingPipeline'
import { segmentsIntersect } from '../../../utils/geometryUtils'
import { SENSOR_CONTEXT_MS } from '../../sensorContextWindow'

export class TelemetryTrimmingStage implements PipelineStage {
  name = 'Telemetry Trimming'

  async execute(context: PipelineContext): Promise<StageResult<void>> {
    const { rawTelemetry, track } = context

    if (!rawTelemetry || !track.finishLine) {
      return { success: true }
    }

    const { gps } = rawTelemetry
    if (gps.length < 2) {
      return { success: true }
    }

    const fl = track.finishLine

    // Find first and last finish line crossings in GPS data
    let firstCrossingIdx = -1
    let lastCrossingIdx = -1
    let crossingCount = 0

    const flStartLat = fl.startPoint.latitude
    const flStartLon = fl.startPoint.longitude
    const flEndLat = fl.endPoint.latitude
    const flEndLon = fl.endPoint.longitude

    for (let i = 0; i < gps.length - 1; i++) {
      if (
        segmentsIntersect(
          gps[i].latitude,
          gps[i].longitude,
          gps[i + 1].latitude,
          gps[i + 1].longitude,
          flStartLat,
          flStartLon,
          flEndLat,
          flEndLon
        )
      ) {
        if (firstCrossingIdx === -1) firstCrossingIdx = i
        lastCrossingIdx = i
        crossingCount++
      }
    }

    // Need at least 2 crossings to form one complete lap
    if (crossingCount < 2) {
      return { success: true }
    }

    // Time bounds with buffer for filter convergence
    const startTimeMs =
      gps[firstCrossingIdx].timestamp.getTime() - SENSOR_CONTEXT_MS
    const endTimeMs =
      gps[lastCrossingIdx + 1].timestamp.getTime() + SENSOR_CONTEXT_MS

    // Trim GPS
    const trimmedGps = trimSortedByTime(gps, startTimeMs, endTimeMs)

    // Not worth trimming if we'd only remove a small fraction
    if (trimmedGps.length >= gps.length * 0.9) {
      return { success: true }
    }

    // Trim accelerometer and gyroscope to same time window
    context.rawTelemetry = {
      ...rawTelemetry,
      gps: trimmedGps,
      accelerometer: rawTelemetry.accelerometer
        ? trimSortedByTime(rawTelemetry.accelerometer, startTimeMs, endTimeMs)
        : undefined,
      gyroscope: rawTelemetry.gyroscope
        ? trimSortedByTime(rawTelemetry.gyroscope, startTimeMs, endTimeMs)
        : undefined,
    }

    return {
      success: true,
      warnings: [
        `Trimmed telemetry from ${gps.length} to ${trimmedGps.length} GPS points ` +
          `(${crossingCount} crossings detected)`,
      ],
    }
  }
}

/**
 * Trim a time-sorted array to [startTimeMs, endTimeMs] using binary search.
 */
function trimSortedByTime<T extends { timestamp: Date }>(
  readings: T[],
  startTimeMs: number,
  endTimeMs: number
): T[] {
  if (readings.length === 0) return readings

  // Binary search for first element >= startTimeMs
  let lo = 0
  let hi = readings.length
  while (lo < hi) {
    const mid = (lo + hi) >> 1
    if (readings[mid].timestamp.getTime() < startTimeMs) {
      lo = mid + 1
    } else {
      hi = mid
    }
  }
  const startIdx = lo

  // Binary search for first element > endTimeMs
  hi = readings.length
  while (lo < hi) {
    const mid = (lo + hi) >> 1
    if (readings[mid].timestamp.getTime() <= endTimeMs) {
      lo = mid + 1
    } else {
      hi = mid
    }
  }
  const endIdx = lo

  // No trimming needed if we'd keep everything
  if (startIdx === 0 && endIdx === readings.length) return readings

  return readings.slice(startIdx, endIdx)
}
